import {
  clamp,
  fairnessMinimumZoom,
  getMinimumZoomForTrackBounds,
  viewportBaseZoom,
  type CameraConfig,
  type TrackBounds,
  type ViewportSize,
  CAMERA_CONFIG,
} from './cameraConfig';

export interface CameraCollisionTelemetry {
  impulse: number;
  tick: number;
}

export class CameraZoomController {
  private readonly config: CameraConfig;
  private viewport: ViewportSize = { width: 640, height: 480 };
  private trackBounds: TrackBounds | null = null;
  private baseZoom: number;
  private fairnessMinimumZoom: number;
  private minimumTrackZoom: number;
  private zoom: number;
  private smoothedSpeedRatio = 0;
  private lastSpeedForTarget = 0;
  private lastSpeedMultiplier = 1;
  private lastCollisionTick: number | null = null;
  private collisionHoldRemainingMs = 0;
  private collisionBoost = 0;
  private reducedMotion = false;

  constructor(config: CameraConfig = CAMERA_CONFIG) {
    this.config = config;
    this.baseZoom = viewportBaseZoom(this.viewport, config);
    this.fairnessMinimumZoom = fairnessMinimumZoom(this.viewport, config);
    this.minimumTrackZoom = config.minZoom;
    this.zoom = this.baseZoom;
  }

  reset(): void {
    this.smoothedSpeedRatio = 0;
    this.lastSpeedForTarget = 0;
    this.lastSpeedMultiplier = 1;
    this.lastCollisionTick = null;
    this.collisionHoldRemainingMs = 0;
    this.collisionBoost = 0;
    this.zoom = this.safeZoom();
  }

  setViewport(viewport: ViewportSize, trackBounds?: TrackBounds): void {
    this.viewport = {
      width: finitePositive(viewport.width, 640),
      height: finitePositive(viewport.height, 480),
    };
    if (trackBounds) this.trackBounds = { ...trackBounds };
    this.baseZoom = viewportBaseZoom(this.viewport, this.config);
    this.fairnessMinimumZoom = fairnessMinimumZoom(this.viewport, this.config);
    this.minimumTrackZoom = this.trackBounds
      ? getMinimumZoomForTrackBounds(this.viewport, this.trackBounds, this.config)
      : this.config.minZoom;
    this.zoom = Math.max(this.zoom, this.baseZoom, this.fairnessMinimumZoom, this.minimumTrackZoom);
    if (this.reducedMotion) this.zoom = this.safeZoom();
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
    if (enabled) {
      this.collisionHoldRemainingMs = 0;
      this.collisionBoost = 0;
      this.zoom = this.safeZoom();
    }
  }

  isReducedMotion(): boolean {
    return this.reducedMotion;
  }

  update(deltaMs: number, speedRatio: number, collision?: CameraCollisionTelemetry): number {
    const delta = clamp(Number.isFinite(deltaMs) ? deltaMs : 0, 0, 1000);
    const speed = clamp(Number.isFinite(speedRatio) ? speedRatio : 0, 0, 1);
    this.smoothedSpeedRatio = damp(
      this.smoothedSpeedRatio,
      speed,
      this.config.speedSmoothingRate,
      delta,
    );
    this.updateCollision(delta, collision);

    const target = this.reducedMotion ? this.safeZoom() : this.targetZoom();
    if (this.reducedMotion) {
      this.zoom = target;
    } else {
      const rate =
        target > this.zoom ? this.config.zoomInDampingRate : this.config.zoomOutDampingRate;
      this.zoom = damp(this.zoom, target, rate, delta);
    }
    this.zoom = this.limitZoom(this.zoom);
    return this.zoom;
  }

  getZoom(): number {
    return this.zoom;
  }

  getBaseZoom(): number {
    return this.baseZoom;
  }

  getTargetZoom(): number {
    return this.reducedMotion ? this.safeZoom() : this.targetZoom();
  }

  getSmoothedSpeedRatio(): number {
    return this.smoothedSpeedRatio;
  }

  private targetZoom(): number {
    const speedMultiplier = this.speedMultiplier();
    const collisionMultiplier = 1 + this.collisionBoost * (this.config.collisionZoomMultiplier - 1);
    return this.limitZoom(this.baseZoom * speedMultiplier * collisionMultiplier);
  }

  private speedMultiplier(): number {
    const speed = this.smoothedSpeedRatio;
    if (Math.abs(speed - this.lastSpeedForTarget) <= this.config.speedDeadBand) {
      return this.lastSpeedMultiplier;
    }
    this.lastSpeedForTarget = speed;
    const span = this.config.speedFullRatio - this.config.speedStartRatio;
    const progress = span > 0 ? clamp((speed - this.config.speedStartRatio) / span, 0, 1) : 1;
    const eased = progress * progress * (3 - 2 * progress);
    this.lastSpeedMultiplier = 1 - eased * (1 - clamp(this.config.highSpeedZoomMultiplier, 0, 1));
    return this.lastSpeedMultiplier;
  }

  private updateCollision(deltaMs: number, collision?: CameraCollisionTelemetry): void {
    if (
      collision &&
      Number.isFinite(collision.impulse) &&
      collision.impulse >= this.config.collisionImpulseThreshold &&
      collision.tick !== this.lastCollisionTick
    ) {
      this.lastCollisionTick = collision.tick;
      this.collisionHoldRemainingMs = this.config.collisionHoldMs;
      this.collisionBoost = 1;
    }

    if (this.collisionHoldRemainingMs > 0) {
      this.collisionHoldRemainingMs = Math.max(0, this.collisionHoldRemainingMs - deltaMs);
      return;
    }
    this.collisionBoost = damp(this.collisionBoost, 0, this.config.collisionRecoveryRate, deltaMs);
  }

  private safeZoom(): number {
    return this.limitZoom(this.baseZoom);
  }

  private limitZoom(value: number): number {
    const minimum = Math.max(this.config.minZoom, this.fairnessMinimumZoom, this.minimumTrackZoom);
    return Math.max(minimum, Math.min(this.config.maxZoom, value));
  }
}

function damp(current: number, target: number, rate: number, deltaMs: number): number {
  if (deltaMs <= 0 || current === target) return current;
  const response = 1 - Math.exp(-Math.max(0, rate) * (deltaMs / 1000));
  return current + (target - current) * response;
}

function finitePositive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}
